"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Home, Megaphone, Users,
  FileText, LogOut, ChevronDown, ChevronsLeft, ChevronsRight, Building2, MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import { getUser, clearUser } from "@/lib/auth";

const INK    = "#1A1814";
const LIME   = "#D5E83F";
const BORDER = "#E7E3DD";
const MUTED  = "#77736B";

const NAV_ITEMS = [
  { icon: Home,          label: "Home",      href: "/dashboard" },
  { icon: Megaphone,     label: "Campaigns", href: "/campaigns" },
  { icon: Users,         label: "Creators",  href: "/creators"  },
  { icon: FileText,      label: "Reports",   href: "/reports"   },
];

export default function AppSidebar() {
  const router   = useRouter();
  const pathname = usePathname();

  const [user, setUser]           = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hasChatSession, setHasChatSession] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleCollapsed = () => {
    setCollapsed(v => {
      const next = !v;
      localStorage.setItem("sidebar_collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    if (pathname === "/dashboard") {
      setCollapsed(true);
      localStorage.setItem("sidebar_collapsed", "true");
    } else {
      setCollapsed(localStorage.getItem("sidebar_collapsed") === "true");
    }
  }, [pathname]);

  useEffect(() => {
    const u = getUser();
    if (!u) return;
    setUser(u);
    api.campaigns.list(u).then(setCampaigns).catch(() => {});
    // Check for an active chat session (written by dashboard page)
    try { setHasChatSession(localStorage.getItem("flintly_chat_active") === "1"); } catch {}
  }, [pathname]);

  // Re-check when dashboard writes the flag (same-tab custom event + cross-tab storage event)
  useEffect(() => {
    const handler = () => {
      try { setHasChatSession(localStorage.getItem("flintly_chat_active") === "1"); } catch {}
    };
    window.addEventListener("flintly_chat_active", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("flintly_chat_active", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const logout = () => { clearUser(); router.replace("/"); };
  const displayName = user ? user.charAt(0).toUpperCase() + user.slice(1) : "";

  const isActive = (href: string) => {
    if (href === "/dashboard") return pathname === "/dashboard";
    return pathname.startsWith(href);
  };

  return (
    <aside
      className="relative flex-shrink-0 flex flex-col h-full transition-all duration-200"
      style={{
        width: collapsed ? "60px" : "228px",
        background: "#FFFFFF",
        borderRight: `1px solid ${BORDER}`,
      }}
    >
      {/* Floating collapse / expand button on the right edge */}
      <button
        onClick={toggleCollapsed}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute top-[52px] -right-3 z-50 w-6 h-6 rounded-full flex items-center justify-center shadow-md transition-colors"
        style={collapsed
          ? { background: INK, color: "#FFFFFF" }
          : { background: "#F0F0F0", color: INK }
        }
      >
        {collapsed
          ? <ChevronsRight className="w-3 h-3" />
          : <ChevronsLeft  className="w-3 h-3" />}
      </button>

      {/* Logo */}
      <div
        className="flex items-center px-3 pt-5 pb-4"
        style={{ minHeight: "64px" }}
      >
        {collapsed ? (
          <div className="w-full flex justify-center">
            <a href="https://flintly.co" target="_blank" rel="noopener noreferrer" title="Flintly">
              <img src="/flintly-logo.png" alt="Flintly" className="w-8 h-8 object-contain" />
            </a>
          </div>
        ) : (
          <a href="https://flintly.co" target="_blank" rel="noopener noreferrer" className="flex items-center shrink-0" title="Flintly">
            <img src="/flintly-wordmark.png" alt="Flintly" className="h-9 w-auto" />
          </a>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 space-y-0.5">
        {NAV_ITEMS.map(({ icon: Icon, label, href }) => {
          const active = isActive(href);
          const isHome = href === "/dashboard";
          const handleClick = isHome
            ? () => {
                // Reset UI to idle without touching stored chat (Chat nav stays visible)
                window.dispatchEvent(new CustomEvent("flintly_dashboard_reset"));
              }
            : undefined;
          return (
            <Link
              key={href}
              href={href}
              title={collapsed ? label : undefined}
              onClick={handleClick}
              className="flex items-center rounded-xl text-[14px] transition-all"
              style={{
                gap: collapsed ? 0 : "10px",
                padding: collapsed ? "10px 0" : "9px 10px",
                justifyContent: collapsed ? "center" : "flex-start",
                ...(active
                  ? { background: LIME, color: INK, fontWeight: 600 }
                  : { color: MUTED, fontWeight: 500 }),
              }}
              onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(26,24,20,0.04)"; }}
              onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = active ? LIME : "transparent"; }}
            >
              <Icon className="w-[18px] h-[18px] flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </Link>
          );
        })}

        {/* Chat — shown when an active session exists */}
        {hasChatSession && (
          <button
            title={collapsed ? "Chat" : undefined}
            onClick={() => {
              if (pathname === "/dashboard") {
                // Already on dashboard — just restore the chat in-place
                window.dispatchEvent(new CustomEvent("flintly_chat_restore"));
              } else {
                router.push("/dashboard");
              }
            }}
            className="flex items-center rounded-xl text-[14px] transition-all w-full"
            style={{
              gap: collapsed ? 0 : "10px",
              padding: collapsed ? "10px 0" : "9px 10px",
              justifyContent: collapsed ? "center" : "flex-start",
              color: MUTED,
              fontWeight: 500,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(26,24,20,0.04)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            <MessageSquare className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && (
              <span className="flex items-center gap-1.5">
                Chat
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              </span>
            )}
          </button>
        )}
      </nav>

      {/* Recent campaigns - hidden when collapsed */}
      {!collapsed && campaigns.length > 0 && (
        <div className="px-3 pb-3 flex flex-col min-h-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-2 px-1 shrink-0"
            style={{ color: "rgba(26,24,20,0.30)", letterSpacing: "0.14em" }}
          >
            Recent campaigns
          </p>
          <div className="overflow-y-auto space-y-0.5 mb-2" style={{ maxHeight: "132px" }}>
            {campaigns.map((c: any) => {
              const name = c.name.charAt(0).toUpperCase() + c.name.slice(1).toLowerCase();
              const status = (c.status || "draft").replace(/_/g, " ");
              return (
                <Link
                  key={c.id}
                  href={`/campaigns/${c.id}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors min-w-0"
                  style={{ color: MUTED }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(26,24,20,0.04)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-300" />
                  <span className="text-[12px] truncate flex-1">{name}</span>
                  <span className="text-[10px] shrink-0 capitalize" style={{ color: "rgba(26,24,20,0.30)" }}>{status}</span>
                </Link>
              );
            })}
          </div>
          <Link
            href="/campaigns"
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors text-[11px] font-semibold"
            style={{ color: MUTED }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(26,24,20,0.04)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            View all campaigns →
          </Link>
        </div>
      )}

      {/* User menu */}
      {user && (
        <div
          className="px-2 py-3 relative"
          style={{ borderTop: `1px solid ${BORDER}` }}
          ref={menuRef}
        >
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="w-full flex items-center rounded-xl px-1 py-1.5 transition-colors hover:bg-gray-50"
            style={{
              gap: collapsed ? 0 : "10px",
              justifyContent: collapsed ? "center" : "flex-start",
            }}
            title={collapsed ? displayName : undefined}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: LIME }}
            >
              <span className="text-[12px] font-black uppercase" style={{ color: INK }}>{user[0]}</span>
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[13px] font-semibold truncate" style={{ color: INK }}>{displayName}</p>
                  <p className="text-[11px]" style={{ color: MUTED }}>Flintly</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: MUTED }} />
              </>
            )}
          </button>

          {menuOpen && (
            <div
              className="absolute bottom-full mb-2 bg-white rounded-xl shadow-lg border py-1 z-50"
              style={{
                borderColor: BORDER,
                ...(collapsed
                  ? { left: "100%", marginLeft: "8px", bottom: "0", marginBottom: "0", width: "44px" }
                  : { left: "8px", right: "8px" }),
              }}
            >
              <button
                onClick={() => { setMenuOpen(false); router.push("/onboarding?edit=1"); }}
                title="Brand profile"
                className="w-full flex items-center transition-colors hover:bg-gray-50"
                style={{
                  color: INK,
                  gap: collapsed ? 0 : "10px",
                  padding: collapsed ? "10px 0" : "10px 16px",
                  justifyContent: collapsed ? "center" : "flex-start",
                }}
              >
                <Building2 className="w-4 h-4 shrink-0" style={{ color: MUTED }} />
                {!collapsed && <span className="text-sm">Brand profile</span>}
              </button>
              <button
                onClick={logout}
                title="Log out"
                className="w-full flex items-center transition-colors hover:bg-gray-50"
                style={{
                  color: INK,
                  gap: collapsed ? 0 : "10px",
                  padding: collapsed ? "10px 0" : "10px 16px",
                  justifyContent: collapsed ? "center" : "flex-start",
                }}
              >
                <LogOut className="w-4 h-4 shrink-0" style={{ color: MUTED }} />
                {!collapsed && <span className="text-sm">Log out</span>}
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
